import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  AlertTriangle,
  MessageSquare,
  Shield,
  CheckCircle2,
  XCircle,
  Pencil,
  Clock,
  Building2
} from "lucide-react";
import { EnterpriseSignal, SignalReviewStatus } from "@/hooks/useEnterpriseSignals";
import { formatDistanceToNow } from "date-fns";

export interface SignalReviewPayload {
  id: string;
  status: Exclude<SignalReviewStatus, 'pending'>;
  notes?: string;
  correctedTitle?: string;
  correctedDescription?: string;
}

interface SignalCardProps {
  signal: EnterpriseSignal;
  onReview?: (payload: SignalReviewPayload) => void;
  isReviewing?: boolean;
}

const categoryIcons = {
  project: AlertTriangle,
  communication: MessageSquare,
  governance: Shield,
};

const categoryColors = {
  project: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  communication: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  governance: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
};

const severityColors = {
  low: 'bg-muted text-muted-foreground',
  medium: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  high: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  critical: 'bg-destructive/10 text-destructive border-destructive/20',
};

const typeLabels: Record<string, string> = {
  status_slip: 'Status Slip',
  dependency_delay: 'Dependency Delay',
  budget_drift: 'Budget Drift',
  risk_surfaced: 'Risk Surfaced',
  resource_conflict: 'Resource Conflict',
  escalation_email: 'Escalation',
  late_approval: 'Late Approval',
  executive_nudge: 'Executive Nudge',
  vendor_pressure: 'Vendor Pressure',
  policy_violation: 'Policy Violation',
  missing_approval: 'Missing Approval',
  sla_breach: 'SLA Breach',
  audit_flag: 'Audit Flag',
};

const reviewStatusLabels: Record<Exclude<SignalReviewStatus, 'pending'>, string> = {
  accepted: 'Accepted',
  rejected: 'Rejected',
  corrected: 'Corrected & Accepted',
};

type PanelMode = null | 'reject' | 'correct';

export function SignalCard({ signal, onReview, isReviewing }: SignalCardProps) {
  const CategoryIcon = categoryIcons[signal.signal_category];
  const [panel, setPanel] = useState<PanelMode>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [correctedTitle, setCorrectedTitle] = useState(signal.title);
  const [correctedDescription, setCorrectedDescription] = useState(signal.description ?? '');
  const [correctionNotes, setCorrectionNotes] = useState('');

  const closePanel = () => {
    setPanel(null);
    setRejectReason('');
    setCorrectionNotes('');
  };

  const handleAccept = () => {
    onReview?.({ id: signal.id, status: 'accepted' });
  };

  const handleConfirmReject = () => {
    if (!rejectReason.trim()) return;
    onReview?.({ id: signal.id, status: 'rejected', notes: rejectReason.trim() });
    closePanel();
  };

  const handleConfirmCorrection = () => {
    if (!correctedTitle.trim()) return;
    onReview?.({
      id: signal.id,
      status: 'corrected',
      notes: correctionNotes.trim() || undefined,
      correctedTitle: correctedTitle.trim(),
      correctedDescription: correctedDescription.trim(),
    });
    closePanel();
  };

  const isTerminal = signal.review_status !== 'pending';

  return (
    <Card className={`transition-all hover:shadow-md ${isTerminal ? 'opacity-60' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-md ${categoryColors[signal.signal_category]}`}>
              <CategoryIcon className="h-4 w-4" />
            </div>
            <div className="flex flex-col">
              <CardTitle className="text-sm font-medium leading-tight">
                {signal.title}
              </CardTitle>
            </div>
          </div>
          <Badge
            variant="outline"
            className={`text-xs shrink-0 ${severityColors[signal.severity]}`}
          >
            {signal.severity}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {signal.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {signal.description}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary" className="text-xs">
            {typeLabels[signal.signal_type] || signal.signal_type}
          </Badge>
          {signal.project_name && (
            <Badge variant="outline" className="text-xs flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              {signal.project_name}
            </Badge>
          )}
        </div>

        {isTerminal && signal.review_notes && (
          <p className="text-xs text-muted-foreground italic border-s-2 ps-2">
            {signal.review_notes}
          </p>
        )}

        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {formatDistanceToNow(new Date(signal.created_at), { addSuffix: true })}
          </div>

          {isTerminal ? (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              {signal.review_status === 'rejected' ? (
                <XCircle className="h-3 w-3 text-destructive" />
              ) : (
                <CheckCircle2 className="h-3 w-3 text-green-600" />
              )}
              {reviewStatusLabels[signal.review_status as Exclude<SignalReviewStatus, 'pending'>]}
            </div>
          ) : onReview && panel === null && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={handleAccept}
                disabled={isReviewing}
              >
                <CheckCircle2 className="h-3 w-3 me-1" />
                Accept
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setPanel('correct')}
                disabled={isReviewing}
              >
                <Pencil className="h-3 w-3 me-1" />
                Correct
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-destructive hover:text-destructive"
                onClick={() => setPanel('reject')}
                disabled={isReviewing}
              >
                <XCircle className="h-3 w-3 me-1" />
                Reject
              </Button>
            </div>
          )}
        </div>

        {panel === 'reject' && (
          <div className="space-y-2 pt-2 border-t">
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Why is this signal not actionable? (required)"
              className="text-xs min-h-16"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={closePanel}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="h-7 text-xs"
                onClick={handleConfirmReject}
                disabled={isReviewing || !rejectReason.trim()}
              >
                Confirm Reject
              </Button>
            </div>
          </div>
        )}

        {panel === 'correct' && (
          <div className="space-y-2 pt-2 border-t">
            <Input
              value={correctedTitle}
              onChange={(e) => setCorrectedTitle(e.target.value)}
              placeholder="Corrected title"
              className="text-xs h-8"
            />
            <Textarea
              value={correctedDescription}
              onChange={(e) => setCorrectedDescription(e.target.value)}
              placeholder="Corrected description"
              className="text-xs min-h-16"
            />
            <Textarea
              value={correctionNotes}
              onChange={(e) => setCorrectionNotes(e.target.value)}
              placeholder="What did you change and why? (optional)"
              className="text-xs min-h-12"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={closePanel}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs"
                onClick={handleConfirmCorrection}
                disabled={isReviewing || !correctedTitle.trim()}
              >
                Save Correction
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
